import PropTypes from "prop-types";
import React, { Component } from "react";
import { Link } from "react-router-dom";

import Entity from "../../shared/components/Entity";
import VirtualizedTable from "../../shared/components/VirtualizedTable";
import { parseName } from "../../shared/utils";
import { ListTableColumnShape, ListTablePolicyShape } from "../proptypes";

class ListViewTable extends Component {
  static orderColumns = (orderBy, columns) =>
    orderBy.map(key => columns.find(column => column.key === key));

  getCellPosition = (column, policy) => {
    const { orderBy, policies } = this.props;
    const cIndex = orderBy.findIndex(col => col === column.key);

    if (policy) {
      const rIndex = policies.findIndex(row => row.$uri === policy.$uri);
      return { cIndex, rIndex };
    }
    return cIndex;
  };

  hideCell = (cIndex, rIndex) => {
    const { orderBy, policies } = this.props;

    if (rIndex <= 0) {
      return false;
    }

    // Check if cells to the left of current cell are hidden
    const cellsLeftHidden = orderBy.every((colKey, i) => {
      if (i < cIndex) {
        return (
          policies[rIndex][colKey].label === policies[rIndex - 1][colKey].label
        );
      }
      return true;
    });
    const cell = policies[rIndex][orderBy[cIndex]];
    const cellAbove = policies[rIndex - 1][orderBy[cIndex]];

    return cellsLeftHidden && cell.label === cellAbove.label;
  };

  handleCheckbox = row => {
    const { policies, orderBy, selectRows } = this.props;
    const firstCol = orderBy[0];
    const policiesGroup = policies.filter(
      policy => policy[firstCol].label === row[firstCol].label
    );

    selectRows(policiesGroup);
  };

  headerRenderer = column => {
    const {
      checkAll,
      disabled,
      policies,
      selectedRows,
      showActions
    } = this.props;
    const cIndex = this.getCellPosition(column);

    if (showActions && cIndex === 0) {
      return (
        <div className="p-table__header-cell">
          <label className="p-checkbox" htmlFor="all-checkbox">
            <input
              id="all-checkbox"
              className="p-checkbox__input"
              type="checkbox"
              disabled={disabled}
              checked={
                selectedRows.length && selectedRows.length === policies.length
              }
              onChange={() => checkAll()}
            />
            <span className="p-checkbox__box" />
            <span className="p-checkbox__text">{column.label}</span>
          </label>
        </div>
      );
    }
    return <div className="p-table__header-cell">{column.label}</div>;
  };

  cellRenderer = (column, policy) => {
    const {
      editRow,
      deleteRow,
      openResources,
      openRoles,
      showActions,
      disabled,
      selectedRows,
      parentUrl
    } = this.props;
    const { key } = column;

    if (showActions && key === "actions") {
      return (
        <div className="p-table__cell--bordered u-align--right">
          <Link
            to="/policies"
            className="p-button--icon u-no-margin"
            onClick={() => editRow(policy)}
          >
            <i className="p-icon--edit" />
          </Link>
          <button
            type="button"
            className="p-button--icon u-no-margin--bottom"
            disabled={disabled}
            onClick={() => deleteRow(policy.$uri)}
          >
            <i className="p-icon--delete" />
          </button>
        </div>
      );
    }
    const cellData = policy[key];
    const { cIndex, rIndex } = this.getCellPosition(column, policy);
    if (!cellData || this.hideCell(cIndex, rIndex)) {
      return "";
    }
    let cellDiv = cellData.label || "";

    if (key === "entity") {
      const { name, domain } = parseName(cellData.label);
      cellDiv = (
        <Link
          to={`${parentUrl}/groups/${name}`}
          className="u-hide-text-overflow"
        >
          <Entity type={cellData.type} domain={domain}>
            {name}
          </Entity>
        </Link>
      );
    } else if (
      key === "service" &&
      cellData.$uri !== "/api/rbac/v1/service/0"
    ) {
      cellDiv = (
        <Link to={`${parentUrl}/services/${cellData.name}`}>
          {cellData.label}
        </Link>
      );
    } else if (key === "resource" && openResources) {
      cellDiv = (
        <button
          type="button"
          className="p-button--link"
          onClick={() => openResources(cellData.type)}
        >
          <span>{cellData.label}</span>
        </button>
      );
    } else if (key === "role" && openRoles) {
      cellDiv = (
        <button
          type="button"
          className="p-button--link"
          onClick={() => openRoles(cellData.$uri)}
        >
          <span>{cellData.label}</span>
        </button>
      );
    }

    if (showActions && cIndex === 0) {
      return (
        <div className="p-table__cell--bordered">
          <label className="p-checkbox" htmlFor={policy.$uri}>
            <input
              id={policy.$uri}
              className="p-checkbox__input"
              type="checkbox"
              disabled={disabled}
              checked={selectedRows.includes(policy)}
              onChange={() => this.handleCheckbox(policy)}
            />
            <span className="p-checkbox__box" />
            <span className="p-checkbox__text" />
          </label>
          {cellDiv}
        </div>
      );
    }
    return <div className="p-table__cell--bordered">{cellDiv}</div>;
  };

  emptyRenderer = () => (
    <div className="p-empty-table">No active policies.</div>
  );

  render() {
    const { columns, orderBy, policies, style } = this.props;
    const orderedColumns = this.constructor.orderColumns(orderBy, columns);

    return (
      <div className="l-listview__table-container" style={style}>
        <VirtualizedTable
          cellRenderer={this.cellRenderer}
          columns={orderedColumns}
          emptyRenderer={this.emptyRenderer}
          headerRenderer={this.headerRenderer}
          rows={policies}
        />
      </div>
    );
  }
}

ListViewTable.defaultProps = {
  checkAll: undefined,
  deleteRow: undefined,
  disabled: false,
  editRow: undefined,
  openRoles: undefined,
  openResources: undefined,
  policies: [],
  selectedRows: [],
  selectRows: undefined,
  showActions: true,
  style: undefined,
  parentUrl: undefined
};

ListViewTable.propTypes = {
  checkAll: PropTypes.func,
  columns: PropTypes.arrayOf(ListTableColumnShape).isRequired,
  deleteRow: PropTypes.func,
  disabled: PropTypes.bool,
  editRow: PropTypes.func,
  openRoles: PropTypes.func,
  openResources: PropTypes.func,
  orderBy: PropTypes.arrayOf(PropTypes.string).isRequired,
  policies: PropTypes.arrayOf(ListTablePolicyShape),
  selectedRows: PropTypes.arrayOf(ListTablePolicyShape),
  selectRows: PropTypes.func,
  showActions: PropTypes.bool,
  style: PropTypes.objectOf(PropTypes.string),
  parentUrl: PropTypes.string
};

export default ListViewTable;
